import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, BookOpen, Check, Search } from 'lucide-react';
import { Course } from '../../../services/api';
import { MixedText } from '../../../utils/rtl';

interface CourseSelectorProps {
  selectedCourseId: number | null;
  newCourseName: string;
  onCourseSelect: (courseId: number | null) => void;
  onNewCourseNameChange: (name: string) => void;
  isCreatingNew: boolean;
  onToggleCreateNew: () => void;
  existingCourses: Course[];
  isRTL: boolean;
  loading?: boolean;
  disabled?: boolean;
}

export const CourseSelector: React.FC<CourseSelectorProps> = ({
  selectedCourseId,
  newCourseName,
  onCourseSelect,
  onNewCourseNameChange,
  isCreatingNew,
  onToggleCreateNew,
  existingCourses,
  isRTL,
  loading = false,
  disabled = false
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedCourse = existingCourses.find(course => course.id === selectedCourseId);

  // Filter courses based on input
  const filteredCourses = existingCourses.filter(course =>
    course.name.toLowerCase().includes(inputValue.toLowerCase())
  );

  // Check if input matches an existing course exactly
  const exactMatch = existingCourses.find(course => 
    course.name.toLowerCase() === inputValue.toLowerCase()
  );

  // Update input value when course is selected externally
  useEffect(() => {
    if (selectedCourse && !isCreatingNew) {
      setInputValue(selectedCourse.name);
    } else if (isCreatingNew) {
      setInputValue(newCourseName);
    }
  }, [selectedCourse, isCreatingNew, newCourseName]);

  // Handle input changes
  const handleInputChange = (value: string) => {
    setInputValue(value);
    setShowDropdown(true);
    setFocusedIndex(-1);

    // Check if typing matches an existing course
    const matchingCourse = existingCourses.find(course => 
      course.name.toLowerCase() === value.toLowerCase()
    );

    if (matchingCourse) {
      // Select existing course
      onCourseSelect(matchingCourse.id);
      if (isCreatingNew) {
        onToggleCreateNew(); // Switch off create mode
      }
    } else {
      // Prepare for new course creation
      onNewCourseNameChange(value);
      if (!isCreatingNew && value.trim().length > 0) {
        onToggleCreateNew(); // Switch to create mode
      } else if (isCreatingNew && value.trim().length === 0) {
        onToggleCreateNew(); // Switch off create mode if input is empty
      }
      onCourseSelect(null);
    }
  };

  // Handle course selection from dropdown
  const handleCourseSelect = (course: Course) => {
    setInputValue(course.name);
    onCourseSelect(course.id);
    if (isCreatingNew) {
      onToggleCreateNew();
    }
    setShowDropdown(false);
    setFocusedIndex(-1);
  };

  // Handle creating new course
  const handleCreateNew = () => {
    onNewCourseNameChange(inputValue);
    if (!isCreatingNew) {
      onToggleCreateNew();
    }
    onCourseSelect(null);
    setShowDropdown(false);
    setFocusedIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) return;

    const options = [...filteredCourses];
    if (inputValue.trim() && !exactMatch) {
      options.push({ id: -1, name: inputValue } as Course); // Placeholder for create new
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => prev < options.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filteredCourses.length) {
          handleCourseSelect(filteredCourses[focusedIndex]);
        } else if (focusedIndex === options.length - 1 && !exactMatch) {
          handleCreateNew();
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setFocusedIndex(-1);
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        dropdownRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
        setFocusedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative">
      <label className={`block text-lg font-semibold text-surface-primary mb-3 font-hebrew ${isRTL ? 'text-right' : ''}`}>
        <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
          📚 {t('courses:create.form.name')} *
        </span>
      </label>

      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setShowDropdown(true)}
          onKeyDown={handleKeyDown}
          placeholder={t('courses:create.form.name_placeholder')}
          disabled={disabled || loading}
          className={`w-full px-4 py-3 border-2 border-surface-muted/30 rounded-lg focus:border-vibrant-orange focus:ring-2 focus:ring-vibrant-orange/20 transition-all duration-200 font-hebrew bg-surface-muted/5 backdrop-blur-sm ${isRTL ? 'text-right pr-12' : 'pl-12'} ${disabled ? 'opacity-50' : ''}`}
          dir={isRTL ? 'rtl' : 'ltr'}
          required
        />
        
        {/* Search/Course icon */}
        <div className={`absolute top-1/2 transform -translate-y-1/2 ${isRTL ? 'right-4' : 'left-4'}`}>
          {isCreatingNew ? (
            <Plus className="w-4 h-4 text-vibrant-orange" />
          ) : (
            <BookOpen className="w-4 h-4 text-surface-secondary" />
          )}
        </div>

        {/* Dropdown */}
        {showDropdown && (inputValue.length > 0 || existingCourses.length > 0) && (
          <div
            ref={dropdownRef}
            className="absolute top-full left-0 right-0 mt-2 bg-astronaut-white border-2 border-surface-muted/30 rounded-lg shadow-xl backdrop-blur-sm z-50 max-h-64 overflow-y-auto"
          >
            {/* Existing courses */}
            {filteredCourses.map((course, index) => (
              <button
                key={course.id}
                type="button"
                onClick={() => handleCourseSelect(course)}
                className={`w-full px-4 py-3 hover:bg-vibrant-orange/10 transition-colors duration-200 border-b border-surface-muted/20 last:border-b-0 ${isRTL ? 'text-right' : 'text-left'} ${
                  focusedIndex === index ? 'bg-vibrant-orange/10' : ''
                }`}
              >
                <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <BookOpen className="w-4 h-4 text-surface-secondary flex-shrink-0" />
                  <span className="font-hebrew ml-3 mr-3 flex-1">
                    <MixedText>{course.name}</MixedText>
                  </span>
                  {selectedCourseId === course.id && (
                    <Check className="w-4 h-4 text-vibrant-orange" />
                  )}
                </div>
              </button>
            ))}

            {/* Create new option */}
            {inputValue.trim() && !exactMatch && (
              <button
                type="button"
                onClick={handleCreateNew}
                className={`w-full px-4 py-3 hover:bg-vibrant-orange/10 transition-colors duration-200 border-t border-surface-muted/20 ${isRTL ? 'text-right' : 'text-left'} ${
                  focusedIndex === filteredCourses.length ? 'bg-vibrant-orange/10' : ''
                }`}
              >
                <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Plus className="w-4 h-4 text-vibrant-orange flex-shrink-0" />
                  <span className="font-hebrew ml-3 mr-3 flex-1">
                    <MixedText>{`${t('common:buttons.create')}: "${inputValue}"`}</MixedText>
                  </span>
                </div>
              </button>
            )}

            {/* No courses found */}
            {filteredCourses.length === 0 && !inputValue.trim() && (
              <div className={`px-4 py-6 text-center ${isRTL ? 'text-right' : ''}`}>
                <BookOpen className="w-8 h-8 text-surface-secondary mx-auto mb-2" />
                <p className="text-surface-secondary font-hebrew text-sm">
                  <MixedText>{t('courses:list.empty.description')}</MixedText>
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Validation message */}
      {isCreatingNew && inputValue.trim().length > 0 && inputValue.trim().length < 3 && (
        <p className={`text-sm text-red-500 mt-1 font-hebrew ${isRTL ? 'text-right' : ''}`}>
          <MixedText>{t('courses:create.errors.name_required')}</MixedText>
        </p>
      )}

      {/* Helper text */}
      <p className={`text-xs text-surface-muted mt-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
        <MixedText>
          {isCreatingNew 
            ? t('courses:create.form.creating_new_hint', { name: inputValue })
            : t('courses:create.form.search_or_create_hint')
          }
        </MixedText>
      </p>
    </div>
  );
}; 